import { useTranslation } from 'react-i18next';
import { type TestPacketsInputs, type TestPacketsResults, type CropConfig, type AreaUnit, type WeightUnit, AREA_FACTORS, WEIGHT_FACTORS, AREA_LABELS, WEIGHT_LABELS, getCropBgColor } from './types';
import { NumberInput } from './NumberInput';
import { formatNumber, formatCurrency } from './utils';
import { CalculationBreakdown } from './CalculationBreakdown';

interface Props {
  inputs: TestPacketsInputs;
  onChange: (inputs: TestPacketsInputs) => void;
  results: TestPacketsResults;
  seedCosts: Record<string, number>;
  seedRates: Record<string, number>;
  crops: CropConfig[];
  currencyCode: string;
  areaUnit: AreaUnit;
  weightUnit: WeightUnit;
}

export function TestPacketsSection({ inputs, onChange, results, seedCosts, seedRates, crops, currencyCode, areaUnit, weightUnit }: Props) {
  const { t } = useTranslation();
  const af = AREA_FACTORS[areaUnit];
  const wf = WEIGHT_FACTORS[weightUnit];
  const aLabel = AREA_LABELS[areaUnit];
  const wLabel = WEIGHT_LABELS[weightUnit];

  const updatePacketWeight = (cropId: string, displayValue: number) => {
    onChange({
      ...inputs,
      packetWeightsKg: { ...inputs.packetWeightsKg, [cropId]: displayValue / wf },
    });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {/* Inputs Card */}
      <div className="card !p-4 md:!p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-spi-orange to-spi-orange-dark flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-display text-spi-green-dark leading-tight">{t('testPackets.inputsTitle')}</h3>
            <p className="text-xs text-spi-slate">{t('testPackets.inputsSubtitle')}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <NumberInput
              label={t('testPackets.refugeeHouseholds')}
              value={inputs.refugeeHouseholds}
              onChange={(v) => onChange({ ...inputs, refugeeHouseholds: v })}
              min={0}
              step={100}
            />
            <NumberInput
              label={t('testPackets.hostCommunityHouseholds')}
              value={inputs.hostCommunityHouseholds}
              onChange={(v) => onChange({ ...inputs, hostCommunityHouseholds: v })}
              min={0}
              step={100}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <NumberInput
              label={t('testPackets.packetsPerRefugee')}
              value={inputs.avgPacketsPerRefugeeHousehold}
              onChange={(v) => onChange({ ...inputs, avgPacketsPerRefugeeHousehold: v })}
              min={0}
              step={0.1}
            />
            <NumberInput
              label={t('testPackets.packetsPerHost')}
              value={inputs.avgPacketsPerHostHousehold}
              onChange={(v) => onChange({ ...inputs, avgPacketsPerHostHousehold: v })}
              min={0}
              step={0.1}
            />
          </div>

          <div className="p-2.5 bg-gradient-to-r from-spi-orange/10 to-spi-orange/5 rounded-lg border border-spi-orange/20 text-center">
            <span className="text-xs text-spi-slate">{t('testPackets.totalTestPackets')} </span>
            <span className="text-base font-bold text-spi-orange-dark">
              {formatNumber(results.totalTestPackets)}
            </span>
          </div>

          <div>
            <label className="input-label">{t('testPackets.packetWeights', { weight: wLabel })}</label>
            <div className="grid grid-cols-2 gap-2">
              {crops.map((crop) => (
                <NumberInput
                  key={crop.id}
                  label={crop.label}
                  value={parseFloat(((inputs.packetWeightsKg[crop.id] ?? 0) * wf).toFixed(4))}
                  onChange={(v) => updatePacketWeight(crop.id, v)}
                  suffix={wLabel}
                  min={0}
                  step={0.001}
                  compact
                  accentColor={crop.color}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results Card */}
      <div className="card !p-4 md:!p-5">
        <h4 className="font-semibold text-spi-charcoal mb-3 flex items-center gap-2 text-sm">
          <svg className="w-4 h-4 text-spi-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          {t('testPackets.resultsTitle')}
        </h4>
        <div className="space-y-2">
          {crops.map((crop) => (
            <div
              key={crop.id}
              className="p-3 rounded-lg border-l-4"
              style={{
                borderLeftColor: crop.color,
                background: `linear-gradient(135deg, ${getCropBgColor(crop.color)} 0%, white 100%)`,
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-spi-charcoal text-sm">{crop.label}</span>
                <CalculationBreakdown
                  type="testPackets"
                  cropLabel={crop.label}
                  cropId={crop.id}
                  inputs={inputs}
                  totalPackets={results.totalTestPackets}
                  seedCostPerKg={seedCosts[crop.id] ?? 0}
                  seedRatePerHa={seedRates[crop.id] ?? 0}
                  currencyCode={currencyCode}
                  areaUnit={areaUnit}
                  weightUnit={weightUnit}
                  result={{
                    seedKg: results.seedRequirementsKg[crop.id] ?? 0,
                    equivalentHa: results.equivalentHectares[crop.id] ?? 0,
                    costLocal: results.costsLocal[crop.id] ?? 0,
                    costUsd: results.costsUsd[crop.id] ?? 0,
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('testPackets.seed')}</span>{' '}
                  <span className="font-medium">{formatNumber((results.seedRequirementsKg[crop.id] ?? 0) * wf)} {wLabel}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('testPackets.equiv')}</span>{' '}
                  <span className="font-medium">{formatNumber((results.equivalentHectares[crop.id] ?? 0) * af)} {aLabel}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('testPackets.cost')}</span>{' '}
                  <span className="font-medium">{formatCurrency(results.costsLocal[crop.id] ?? 0, currencyCode)}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('testPackets.usd')}</span>{' '}
                  <span className="font-medium">{formatCurrency(results.costsUsd[crop.id] ?? 0, 'USD')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 p-3 bg-gradient-to-r from-spi-orange/10 to-spi-orange/5 rounded-lg border border-spi-orange/20">
          <div className="font-bold text-spi-earth mb-2 text-sm">{t('testPackets.totalTitle')}</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-spi-slate">{t('testPackets.totalSeed')}</span>{' '}
              <span className="font-semibold">{formatNumber(results.totalSeedKg * wf)} {wLabel}</span>
            </div>
            <div>
              <span className="text-spi-slate">{t('testPackets.packets')}</span>{' '}
              <span className="font-semibold">{formatNumber(results.totalTestPackets)}</span>
            </div>
            <div>
              <span className="text-spi-slate">{t('testPackets.totalCost')}</span>{' '}
              <span className="font-semibold">{formatCurrency(results.totalCostLocal, currencyCode)}</span>
            </div>
            <div>
              <span className="text-spi-slate">{t('testPackets.usd')}</span>{' '}
              <span className="font-semibold">{formatCurrency(results.totalCostUsd, 'USD')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
